import { shareOrigin, type ShareChannel } from '@/components/accessories/accessoryShare';
import { company } from '@/config/company';
import { type VehicleListing } from '@/services/vehicles/types';
import {
  availabilityLabels,
  countryName,
  formatEngine,
  formatMileage,
  formatUgx,
  formatUsd,
  fuelLabels,
  transmissionLabels,
  vehiclePath,
  vehicleTitle,
} from '@/utils/vehicleFormat';

export { shareOrigin };

/** X counts every link as 23 characters, plus the space before it. */
const X_LIMIT = 280;
const X_LINK_COST = 24;

/** Where the shared link lands, tagged so a visit can be traced to its channel. */
export function vehicleShareUrl(vehicle: Pick<VehicleListing, 'slug'>, channel: ShareChannel, origin: string): string {
  const params = new URLSearchParams({ utm_source: channel, utm_medium: 'share' });
  return `${origin}${vehiclePath(vehicle)}?${params.toString()}`;
}

const priceLine = (v: VehicleListing) =>
  v.price.usd !== null
    ? `${formatUsd(v.price.usd)}${v.price.terms ? ` ${v.price.terms.toUpperCase()}` : ''}`
    : v.price.ugx !== null
      ? formatUgx(v.price.ugx)
      : 'Price on request';

const specLine = (v: VehicleListing) =>
  [
    formatMileage(v.mileageKm),
    formatEngine(v.engineCc),
    v.transmission ? transmissionLabels[v.transmission] : null,
    v.fuelType ? fuelLabels[v.fuelType] : null,
  ]
    .filter(Boolean)
    .join(' · ');

/** +256780905191 → '+256 780 905 191'; anything else is shown as configured. */
function whatsappNumberLine(): string | null {
  const e164 = company.whatsappE164;
  if (!e164) return null;
  const grouped = e164.match(/^\+256(\d{3})(\d{3})(\d{3})$/);
  return `Ask or order on WhatsApp: ${grouped ? `+256 ${grouped[1]} ${grouped[2]} ${grouped[3]}` : e164}`;
}

/**
 * The message shared on WhatsApp and copied by Copy message, one fact per line.
 * Asterisks are WhatsApp's bold; the link is last so WhatsApp draws the photo
 * beneath it. No emoji, which can arrive as replacement characters.
 */
export function vehicleShareMessage(vehicle: VehicleListing, link: string): string {
  return [
    `*${vehicleTitle(vehicle)}*`,
    [vehicle.trimLevel, `From ${countryName(vehicle.sourceCountry)}`].filter(Boolean).join(' · '),
    specLine(vehicle) || null,
    `*${priceLine(vehicle)}*`,
    availabilityLabels[vehicle.availability],
    '',
    'Kabex Motors, Kampala. We import, clear and deliver anywhere in Uganda.',
    '',
    'See photos and full details:',
    link,
    whatsappNumberLine(),
  ]
    .filter((line): line is string => line !== null && line !== undefined)
    .join('\n');
}

/** The short form for X: one sentence, with the link supplied separately. */
export function vehicleXText(vehicle: VehicleListing): string {
  return `${vehicleTitle(vehicle)} — ${priceLine(vehicle)}. Available with Kabex Motors, Kampala.`;
}

const truncate = (text: string, max: number) =>
  text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;

export function vehicleWhatsappShareHref(vehicle: VehicleListing, origin: string): string {
  const link = vehicleShareUrl(vehicle, 'whatsapp', origin);
  return `https://wa.me/?text=${encodeURIComponent(vehicleShareMessage(vehicle, link))}`;
}

export function vehicleXShareHref(vehicle: VehicleListing, origin: string): string {
  const params = new URLSearchParams({
    text: truncate(vehicleXText(vehicle), X_LIMIT - X_LINK_COST),
    url: vehicleShareUrl(vehicle, 'x', origin),
  });
  return `https://x.com/intent/post?${params.toString()}`;
}
